/**
 * Run an async worker over a list with a bounded number of in-flight tasks.
 *
 * Results are returned in the same order as `items`, regardless of the order in
 * which they settle. Used by the CSV exports, which need to pull hundreds of
 * pages from the API without firing them all at once (and without the
 * one-at-a-time waterfall that made an 84k-user export take minutes).
 *
 * @param {T[]} items work list
 * @param {number} limit maximum number of concurrent workers
 * @param {Function} worker receives an item and its index, returns a promise
 * @returns {Promise<R[]>} results in `items` order
 */
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  if (items.length === 0) return results;

  const workers = Math.max(1, Math.min(Math.floor(limit) || 1, items.length));
  let cursor = 0;

  async function drain() {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;
      results[index] = await worker(items[index], index);
    }
  }

  await Promise.all(Array.from({ length: workers }, () => drain()));
  return results;
}

/**
 * Yield to the event loop so the browser can paint. Called between chunks of a
 * long synchronous transform so the tab stays responsive and progress updates
 * are actually visible instead of arriving all at once at the end.
 */
export function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}
